import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, Loader2, CheckCircle2, Circle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '');

const AdminContactMessages = ({ onChanged }) => {
  const [messages, setMessages] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/contact/messages`, { withCredentials: true });
      setMessages(res.data.messages || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.status === 403 ? 'Administrator access required.' : 'Could not load messages.');
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (m) => {
    try {
      await axios.patch(`${API}/api/contact/messages/${m.id}/handled`, { handled: !m.handled }, { withCredentials: true });
      setMessages((list) => list.map((x) => (x.id === m.id ? { ...x, handled: !m.handled } : x)));
      onChanged?.();
    } catch (_) {
      toast.error('Could not update message');
    }
  };

  if (error) return <p className="text-sm text-red-400" data-testid="admin-messages-error">{error}</p>;
  if (!messages) return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const shown = messages.filter((m) => (filter === 'all' ? true : filter === 'open' ? !m.handled : m.handled));

  return (
    <div className="space-y-4" data-testid="admin-messages">
      <div className="flex items-center gap-2 text-xs">
        {['all', 'open', 'handled'].map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)} className={`px-2.5 py-1 rounded-full border capitalize ${filter === f ? 'border-amber-400 text-amber-300 bg-amber-500/10' : 'border-slate-600 text-slate-400'}`} data-testid={`messages-filter-${f}`}>{f}</button>
        ))}
        <span className="text-slate-500 ml-auto">{shown.length} of {messages.length}</span>
      </div>
      {shown.length === 0 ? (
        <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-10 text-center text-slate-400" data-testid="messages-empty"><Mail className="h-8 w-8 mx-auto mb-3 opacity-60" /> No messages.</div>
      ) : shown.map((m) => (
        <div key={m.id} className={`rounded-xl border p-4 sm:p-5 ${m.handled ? 'border-slate-800 bg-slate-800/30' : 'border-slate-700 bg-slate-800/50'}`} data-testid="message-card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="min-w-0">
              <p className="font-semibold text-white truncate" data-testid="message-subject">{m.subject}</p>
              <p className="text-sm text-slate-400 truncate">{m.name} &lt;<span className="text-amber-300">{m.email}</span>&gt;</p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-xs text-slate-500">{fmtDate(m.created_at)}</span>
              <Button size="sm" variant="outline" onClick={() => toggle(m)} className="text-xs" data-testid="message-toggle-handled">
                {m.handled ? <><CheckCircle2 className="h-3.5 w-3.5 mr-1 text-emerald-400" /> Handled</> : <><Circle className="h-3.5 w-3.5 mr-1" /> Mark handled</>}
              </Button>
            </div>
          </div>
          <p className="mt-3 text-sm text-slate-300 whitespace-pre-wrap break-words">{m.message}</p>
        </div>
      ))}
    </div>
  );
};

export default AdminContactMessages;
